import { useMemo } from 'react';

import animationData from '@/assets/lotties/eligibility-loader.json';
import { Heading1 } from '@/components/common/Heading1.tsx';
import { TextRotator } from '@/components/common/TextRotator.tsx';
import { Lottie } from '@/components/common/lottie-web/Lottie.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { prettyTypeName } from '@/types/insurance.type.ts';

const getDiscoveryMessages = (carrierNames: string) => [
  `Confirming your enrollment with ${carrierNames}...`,
  "We're looking up your benefits...",
  'Reviewing your information...',
  `Checking your ${carrierNames} status...`,
  'Confirming what supplies are covered...',
  'Almost finished...',
  'Thank you for your patience. We are still confirming your coverage...',
];

const useDiscoveryMessages = () => {
  const carrierTypes = useSessionStore((state) => state.insuranceBuilder.carrierTypes);

  return useMemo(() => {
    const carrierNames = carrierTypes.map(prettyTypeName).join(' & ');
    return getDiscoveryMessages(carrierNames);
  }, [carrierTypes]);
};

export default function InsuranceDiscoveryLoading() {
  const messages = useDiscoveryMessages();

  return (
    <div
      className="flex flex-col items-center justify-center space-y-8 sm:space-y-12"
      aria-label="Loading..."
      aria-describedby="insurance-check-loader"
      aria-busy="true"
      role="status"
    >
      <Heading1 className="pb-0 text-center">Looking up your benefits...</Heading1>

      <Lottie animationData={animationData} className="aspect-square size-[80px]" />

      <div className="flex flex-col items-center gap-4">
        <TextRotator messages={messages} />
        <p className="text-charcoal-500 text-center text-sm">This may take a minute.</p>
      </div>
    </div>
  );
}
